import {defaultKPIS, harvestVolumeChartKPIs} from 'appConstants';

import {ForecastGetColumnsEnum, type NavigationTreePlan} from 'client';
import {buildSiteForecastResourceId} from 'components/OverviewTable/helpers';
import {buildSWRKey, mutateAll, mutateAllByValue} from 'hooks/swr/swrHookFactory';
import * as I from 'interfaces';
import {mutate} from 'swr';
import {localDateSender} from './UnitsFormatHandler';
import {getMutateDelay} from 'utils/runtimeVars';

const columns = [ForecastGetColumnsEnum.Biomass, ForecastGetColumnsEnum.Count, ForecastGetColumnsEnum.Weight];

const refreshHandler = ({
  planId,
  siteId,
  cageId,
  startDate,
  endDate,
  filteredSites,
}: {
  planId: string | null;
  siteId?: string | null;
  cageId?: string | null;
  startDate: Date;
  endDate: Date;
  filteredSites: string[];
}) => {
  planId && mutatePlan(startDate, endDate, filteredSites);
  siteId && mutateSite(siteId);
  cageId && mutateCage(cageId);
};
const mutatePlan = (startDate: Date, endDate: Date, filteredSites: string[]) => {
  const {
    planId,
    URLPlans,
    shortTermResolution,
    longTermResolution: URLlongTermResolution,
  } = URLHandler(new URLSearchParams(window.location.search));
  const longTermResolution = URLlongTermResolution || I.ResolutionEnum.week;
  mutate(planId);
  mutate([planId, I.SWRKeyEnum.GET_PLAN]);

  // refresh MAB utilization
  mutate([
    URLPlans[0],
    URLPlans[1],
    URLPlans[2],
    URLPlans[3],
    localDateSender(startDate),
    localDateSender(endDate),
    longTermResolution,
    I.SWRKeyEnum.GET_MAB_UTILIZATION_PER_PLAN,
  ]);

  // refresh KPIs table
  defaultKPIS &&
    mutate([
      ...buildSWRKey([URLPlans, startDate, endDate, defaultKPIS, [], filteredSites]),
      I.SWRKeyEnum.GET_PLANS_KPIs,
    ]);

  // refresh harvest volume chart
  harvestVolumeChartKPIs &&
    mutate([
      ...buildSWRKey([URLPlans, startDate, endDate, harvestVolumeChartKPIs, longTermResolution, filteredSites]),
      I.SWRKeyEnum.GET_PLANS_KPIs,
    ]);

  mutateWithDelay([I.SWRKeyEnum.GET_ESTIMATED_NET_VALUE, I.SWRKeyEnum.GET_MAB_UTILIZATION_PER_CONCESSION]);
  mutate([planId, I.SWRKeyEnum.GET_PLAN_HARVESTS]);
  mutateAllTransfers();
};

export const mutateSite = (siteId: string) => {
  void mutateAllByValue(siteId);
};

export const mutateSites = (treePlan: NavigationTreePlan, startDate: Date, endDate: Date) => {
  treePlan.sites?.forEach((site) => {
    mutate([
      ...buildSWRKey(buildSiteForecastResourceId(site.id, startDate, endDate, I.ResolutionEnum.day, columns)),
      I.SWRKeyEnum.GET_SITE_CAGE_FORECAST,
    ]);
    mutate([site.id, I.SWRKeyEnum.GET_SITE_FALLOWINGS]);
  });
};

export const mutateCage = (cageId: string) => {
  const {planId} = URLHandler(new URLSearchParams(window.location.search));

  void mutateAllByValue(cageId);
  void mutate([planId, I.SWRKeyEnum.GET_PLAN_ACTIONS]);
};

export const mutateAllTransfers = () => {
  void mutateAll(I.SWRKeyEnum.GET_PLAN_ACTIONS);
  void mutateAll(I.SWRKeyEnum.GET_PLAN_TRANSFERS);
  void mutateAll(I.SWRKeyEnum.GET_SITE_TRANSFERS);
  void mutateAll(I.SWRKeyEnum.GET_TRANSFER);
};

export const mutateTransfer = (
  planId: string,
  fromSite: string,
  toSite: string,
  cagesIds: string[],
  startDate: Date,
  endDate: Date,
) => {
  cagesIds.forEach((cageId) => {
    mutateCage(cageId);
  });
  mutateSite(fromSite);
  mutateSite(toSite);
  mutate([planId, I.SWRKeyEnum.GET_CAGES_WITH_UNALLOCATED_BIOMASS]);
};

export default refreshHandler;

export const URLHandler = (urlSearchParams: URLSearchParams) => {
  const URLPlans: string[] = urlSearchParams.getAll(I.URLParams.plan);
  const planId = URLPlans[0];
  const shortTermResolution = urlSearchParams.get(I.URLParams.shortTermResolution) as I.ResolutionEnum;
  const longTermResolution = urlSearchParams.get(I.URLParams.longTermResolution) as I.ResolutionEnum;
  const overviewTableResolution = urlSearchParams.get(I.URLParams.tableResolution) as I.ResolutionEnum;

  return {
    planId,
    URLPlans,
    shortTermResolution: shortTermResolution || I.ResolutionEnum.day,
    longTermResolution: longTermResolution || I.ResolutionEnum.week,
    overviewTableResolution: overviewTableResolution || I.ResolutionEnum.day,
  };
};

declare global {
  interface Window {
    timeoutId?: ReturnType<typeof setTimeout>;
  }
}

export const mutateWithDelay = (swrKeys: I.SWRKeyEnum[]) => {
  if (window.timeoutId) {
    clearTimeout(window.timeoutId);
  }
  window.timeoutId = setTimeout(() => {
    swrKeys.forEach((swrKey) => {
      void mutateAll(swrKey);
    });
  }, getMutateDelay('0'));
};
